import { FilterParam } from '../types';
import { expandAssociationTypeValue } from './associationTypeOptions';
import buildBaseEndpoint from './buildBaseEndpoint';

export function getDownloadColumnsMap() {
  const keyValue: [string, string][] = [
    // disease (col_sel must match API response keys so backend can fill TSV)
    ['numVariantsAssociatedToDisease', 'numVariantsAssociatedToDisease'],
    ['numGenesAssociatedToDisease', 'numGenesAssociatedToDisease'],
    ['disease_prevalence_class', 'disease_prevalence_class'],
    ['disease_prevalence_type', 'disease_prevalence_type'],
    ['disease_prevalence_geo_area', 'disease_prevalence_geo_area'],
    ['disease_inheritance', 'disease_inheritance'],
    ['numChemicals', 'numChemicals'],
    // drugs / chem/all (col_sel matches API response keys)
    ['chemID', 'chemID'],
    ['chemPrefName', 'chemPrefName'],
    ['chemMoleculeType', 'chemMoleculeType'],
    ['chemNumGDAs', 'chemNumGDAs'],
    ['chemNumPublicationsGDAs', 'chemNumPublicationsGDAs'],
    ['chemNumVDAs', 'chemNumVDAs'],
    ['chemNumPublicationsVDAs', 'chemNumPublicationsVDAs'],
    ['chemNumPublications', 'chemNumPublications'],
    // gda/summary
    ['chemIDs', 'chem_cuis'],
    ['diseaseUMLSCUI', 'diseaseUMLSCUI'],
    ['geneNcbiID', 'gene_ncbi'],
    ['geneDSI', 'DSI'],
    ['geneDPI', 'DPI'],
    ['genepLI', 'pli'],
    ['EL', 'EL'],
    ['EI', 'ei'],
    ['geneProteinClassNames', 'gp_class_names'],
    // gda/evidences
    ['associationType', 'association_type'],
    // vda/summary
    ['variantStrID', 'variant_name'],
    ['variantDSI', 'dsi'],
    ['variantDPI', 'dpi'],
    ['diseaseName', 'disease_name'],
    ['geneNcbiIDs', 'gene_ncbi'],
    ['gene_ensembl_id', 'gene_ensembl_id'],

    // vda/evidences
    ['diseaseType', 'disease_type'],
    ['geneSymbol', 'geneSymbol'],
    ['mostSevereConsequences', 'mostSevereConsequences'],
    ['sentenceHTML', 'sentence_html'], //'sentence_text'
    ['text', 'text'],
    ['clinical_significance', 'clinical_significance'],
    ['association', 'association'],
    ['citation_count', 'citation_count'],

    // disease class / disease/all (use same keys as API response for correct TSV export)
    ['diseaseClassName_MSH', 'diseaseClassName_MSH'],
    ['diseaseClassName_UMLS', 'diseaseClassName_UMLS'],
    ['diseaseClassName_DO', 'diseaseClassName_DO'],
    ['diseaseClassName_HPO', 'diseaseClassName_HPO'],
    // variants/all
    ['strID', 'strID'],
    ['DSI', 'DSI'],
    ['DPI', 'DPI'],
    ['DBSNPclass', 'DBSNPclass'],
    ['Alleles', 'alleles'],
    ['genome', 'genome'],
    ['exome', 'exome'],
    ['numDiseasesAssociatedToVariant', 'numDiseasesAssociatedToVariant'],
    ['numPublications', 'numPublications'],
    ['firstRef', 'firstRef'],
    ['lastRef', 'lastRef'],
    ['gene_ncbi_id', 'gene_ncbi_id'],
    ['gene_ensembl_id', 'gene_ensembl_id'],
    // gene/all (col_sel matches API response keys)
    ['symbol', 'symbol'],
    ['description', 'description'],
    ['proteins', 'protein'],
    ['PLI', 'pli'],
    ['GTP', 'GTP'],
    ['ncbiID', 'gene_ncbi'],
    ['proteinIDs', 'proteinIDs'],
    ['proteinClassIDs', 'proteinClassIDs'],
    ['pathwayName', 'pathwayName'],
    ['pathwayID', 'pathwayID'],
    ['numDiseasesAssociatedToGene', 'num_diseases'],
    ['numVariantsAssociatedToGene', 'numVariantsAssociatedToGene'],
    ['ncbi_type', 'ncbi_type'],
    ['gene_ensembl_id', 'gene_ensembl_id'],
    // gda/all
    ['geneProteinStrIDs', 'genep_str_ids'],
    //
    ['score', 'score'],
    ['numPMIDsupportingAssociation', 'num_pmid_assoc'],
    ['numberChemsIncludedInEvidence', 'numberChemsIncludedInEvidence'],
    ['numberPmidsWithChemsIncludedInEvidence', 'numberPmidsWithChemsIncludedInEvidence'],
    ['numberNCTSWithChemsIncludedInEvidence', 'numberNCTSWithChemsIncludedInEvidence'],
    ['numDBSNPsupportingAssociation', 'num_var_dis'],
    ['yearInitial', 'year_initial'],
    ['yearFinal', 'year_final'],
    ['ei', 'ei'],
    ['geneNcbiType', 'gene_type'],
    ['reference', 'reference'],
    ['reference_type', 'reference_type'],
    ['icite_apt', 'icite_apt'],
  ];

  return new Map(keyValue);
}

function getViewFilterParam(view: string, tab: string): FilterParam | null {
  switch (view) {
    case 'GENES':
      return tab === 'GENES' ? 'gene_ncbi_id_filter' : 'gene_ncbi_id';
    case 'DISEASES':
      return tab === 'DISEASES' ? 'disease_filter' : 'disease';
    case 'VARIANTS':
      return tab === 'VARIANTS' ? 'variant_name' : 'variant';
    case 'CHEMICALS':
      return tab === 'CHEMICALS' ? 'chem_ids_filter' : 'chem_ids_init';
    default:
      return null;
  }
}

function diseasePrefixedValue(filterParam: string, filterValue: string | number): string {
  if (filterParam !== 'disease') return String(filterValue);
  const value = String(filterValue);
  return value.startsWith('UMLS_') ? value : `UMLS_${value}`;
}

function applyIdentifierParams({
  paramsMap,
  view,
  tab,
  searchItems,
  isMapping,
  mappingContent,
}: {
  paramsMap: Record<string, unknown>;
  view: string;
  tab: string;
  searchItems: Array<{ id?: string | number }>;
  isMapping?: boolean;
  mappingContent?: {
    mappingItems?: Array<{
      filterParam?: FilterParam;
      filterValue?: string | number;
      id?: string | number;
    }>;
  } | null;
}) {
  if (isMapping && mappingContent?.mappingItems) {
    mappingContent.mappingItems.forEach(item => {
      const filterParam = item.filterParam;
      if (!filterParam) return;

      const rawValue = item.filterValue ?? item.id;
      if (rawValue === undefined || rawValue === null || rawValue === '') return;

      const value = diseasePrefixedValue(filterParam, rawValue);
      if (paramsMap[filterParam]) {
        paramsMap[filterParam] = `${paramsMap[filterParam]},${value}`;
      } else {
        paramsMap[filterParam] = value;
      }
    });
    return;
  }

  const filterParam = getViewFilterParam(view, tab);
  if (!filterParam) return;

  const backendDiseaseVocabPrefix = filterParam === 'disease' ? 'UMLS_' : '';
  const filterValue = searchItems.map(item => backendDiseaseVocabPrefix + item.id).join(',');
  if (filterValue) {
    paramsMap[filterParam] = filterValue;
  }
}

function appendQueryParams(paramsMap: Record<string, unknown>): string {
  const queryParams = new URLSearchParams();
  Object.entries(paramsMap).forEach(([param, value]) => {
    if (value === null || value === undefined || value === '') return;
    const strVal = String(value);
    if (param === 'chem_ids_filter' && strVal.includes(',')) {
      strVal.split(',').forEach(id => queryParams.append(param, id.trim()));
    } else {
      queryParams.append(param, strVal);
    }
  });
  return queryParams.toString().replace(/%2C/g, ',').replace(/%3A/g, ':');
}

export function buildDownloadEndpoint({
  extension,
  columnVisibilityModel,
  view,
  source,
  tab,
  searchItems,
  isEvidence,
  isMapping,
  mappingContent,
}) {
  const colMap = getDownloadColumnsMap();

  const selectedColumns = Object.entries(columnVisibilityModel)
    .filter(([key, value]) => key !== 'evidenceTimeline' && value)
    .map(([key]) => colMap.get(key) || key);

  const col_sel = selectedColumns.join(',');

  const paramsMap: Record<string, unknown> = {
    source: source,
    col_sel: col_sel,
  };

  applyIdentifierParams({
    paramsMap,
    view,
    tab,
    searchItems,
    isMapping,
    mappingContent,
  });

  const baseEndpoint = buildBaseEndpoint({
    view,
    tab,
    isEvidence,
    isMapping,
    mappingContent,
  });

  let endPoint = `${baseEndpoint}/${extension}?${appendQueryParams(paramsMap)}`;
  // override for chem downloads
  // the format for chem downloads is inconsitent with the other endpoints
  // 1 solution for the backend could be to remove "/all" from the url patterns
  // alternatively, nest tsv/excel under /all instead of as a sibling
  endPoint = endPoint.replace('chem/gda/all/', 'chem/gda/');

  return endPoint;
}

export function buildDownloadFiltersEndpoint({
  extension,
  columnVisibilityModel,
  view,
  source,
  tab,
  searchItems,
  isEvidence,
  isMapping,
  mappingContent,
  filters = {},
  sourceWeights = [],
}) {
  const colMap = getDownloadColumnsMap();

  const selectedColumns = Object.entries(columnVisibilityModel)
    .filter(([key, value]) => key !== 'evidenceTimeline' && value)
    .map(([key]) => colMap.get(key) || key);

  const col_sel = selectedColumns.join(',');

  const paramsMap: Record<string, any> = {
    source: source,
    col_sel: col_sel,
    ...filters,
  };

  applyIdentifierParams({
    paramsMap,
    view,
    tab,
    searchItems,
    isMapping,
    mappingContent,
  });

  if (paramsMap.association_type) {
    paramsMap.association_type = expandAssociationTypeValue(paramsMap.association_type);
  }

  if (sourceWeights && sourceWeights.length > 0) {
    const weightsString = sourceWeights.map(sw => `${sw.name}:${sw.score}`).join(',');
    paramsMap.source_weights = weightsString;
  }

  if (
    isMapping &&
    mappingContent &&
    (mappingContent as { name?: string }).name === 'numPMIDsupportingAssociation'
  ) {
    const selectedReferenceType = (mappingContent as { selectedReferenceType?: string })
      .selectedReferenceType;
    const selectedPolarity = (mappingContent as { selectedPolarity?: string }).selectedPolarity;

    if (selectedReferenceType === 'PMID' || selectedReferenceType === 'NCTID') {
      if (!paramsMap.reference_type) {
        paramsMap.reference_type = selectedReferenceType;
      }
    }

    if (selectedPolarity === 'Positive' || selectedPolarity === 'Negative') {
      if (!paramsMap.association) {
        paramsMap.association = selectedPolarity;
      }
    }
  }

  const baseEndpoint = buildBaseEndpoint({
    view,
    tab,
    isEvidence,
    isMapping,
    mappingContent,
  });

  let endPoint = `${baseEndpoint}/${extension}?${appendQueryParams(paramsMap)}`;

  endPoint = endPoint.replace('chem/gda/all/', 'chem/gda/');

  return endPoint;
}
